import { CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Listy, Spin, Tooltip, Typography } from 'antd';
import api from 'api';
import { inviteStatusIcons } from 'helpers/inviteIcons';
import { TFunction } from 'i18next';
import { IDataRoom, InviteStatusEnum, IPendingInviteUsers, IRestrictedUser } from 'interfaces';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDeletedInviteId, setPendingInviteUsers } from 'store/slices/dataUsersSlice';
import classes from './ContactsTab.module.scss';
import { ConfirmModal } from 'components/Modals';

type WithInviteStatusRoom = IDataRoom & { invite_status: InviteStatusEnum };

type Props = {
  rooms: IDataRoom[];
  selectedUser: IRestrictedUser;
  initialInvites: IPendingInviteUsers[];
  t: TFunction;
};

export default function InviteRooms({ selectedUser, rooms, t, initialInvites }: Props) {
  const [invites, setInvites] = useState<IPendingInviteUsers[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<'delete' | 'invite'>('invite');
  const [selectedItem, setSelectedItem] = useState<WithInviteStatusRoom | null>(null);

  const { pendingInviteUsers, deletedInviteId } = useAppSelector((state) => state.dataUsers);
  const { messageApi } = useAppSelector((state) => state.windowState);
  
  const dispatch = useAppDispatch();

  useEffect(() => {
    setInvites(initialInvites);
  }, []);

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const response = await api.getInvitesByUser(selectedUser.id);
      setInvites(response.data as IPendingInviteUsers[]);
      return response.data as IPendingInviteUsers[];
    } catch (err) {
      console.log(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const invite = async () => {
    try {
      setLoading(true);
      const groupsResponce = await api.getGroups(selectedItem?.id!);
      const group = groupsResponce.data.find(({ group_name }) => group_name === 'user') || groupsResponce.data[0];
      await api.getNewInvite({
        emails: [selectedUser.email],
        room_id: selectedItem?.id!,
        type: 'common',
        group_id: group.group_id,
        details: {
          emails: [selectedUser.email],
          group_name: group.group_name,
          group_role: group.group_name,
          group_id: group.group_id,
        },
      });

      const response = await fetchInvites();
      dispatch(
        setPendingInviteUsers([...pendingInviteUsers, response?.find(({ room_id }) => room_id === selectedItem?.id)!])
      );
      messageApi?.success(`${t('Documents.success.inviteUser')} ${selectedUser.email}`);
    } catch (err) {
      messageApi?.error(t('Documents.error.inviteUsers'));
    } finally {
      setLoading(false);
      onCLose();
    }
  };

  const reInvite = async (room: WithInviteStatusRoom) => {
    try {
      setLoading(true);
      await api.reSendInvite({invite_id: room.invite_id!});
      setInvites((prev) =>
        prev.map((invite) => (invite.room_id === room.id ? { ...invite, invite_status: 0 } : invite))
      );
      const newPendingUsers = pendingInviteUsers.map((user) =>
        user.id === user.id ? { ...user, invite_status: 0 } : user
      );
      dispatch(setPendingInviteUsers(newPendingUsers));
    } catch (err) {
      messageApi?.error(t('Users.invite.failedReinvite'));
    } finally {
      setLoading(false);
    }
  };

  const deleteInvite = async () => {
    try {
      setLoading(true);
      await api.deleteInvite(selectedItem?.invite_id!);
      setInvites((prev) => prev.filter(({ id }) => id !== selectedItem?.invite_id!));
      const filteredUsers = pendingInviteUsers.filter(({ id }) => id !== selectedItem?.invite_id!);
      dispatch(setPendingInviteUsers(filteredUsers));
    } catch (err) {
      messageApi?.error(t('Users.invite.failedDeleteInvite'));
    } finally {
      setLoading(false);
      onCLose();
    }
  };

  const dataSource = useMemo(() => {
    let mergedInvites = invites.map((invite) => pendingInviteUsers.find(({ id }) => id === invite.id) || invite);
    if (deletedInviteId) {
      mergedInvites = mergedInvites.filter(({ id }) => id !== deletedInviteId);
      dispatch(setDeletedInviteId(''));
    }
    const roomList = rooms.map((room) => {
      const sameInvite = mergedInvites.find((invite) => invite.room_id === room.id);

      return sameInvite ? { ...room, invite_status: sameInvite.invite_status, invite_id: sameInvite.id } : room;
    });

    return roomList as WithInviteStatusRoom[];
  }, [rooms, invites, pendingInviteUsers]);  

  const onCLose = () => setIsOpenConfirm(false);
  const onConfirmOpen = (status: 'delete' | 'invite', item: WithInviteStatusRoom) => {
    setSelectedItem(item);
    setIsOpenConfirm(true);
    setConfirmStatus(status);
  };

  const conFirmCases = {
    delete: {
      body: t('Users.modals.invite.deleteInvite'),
      action: deleteInvite,
    },

    invite: {
      body: t('Users.modals.invite.invitations'),
      action: invite,
    },
  };

  return (
    <>
      <div className={classes.common_length}>
        <b>
          {t('Users.HelperMenu.length')}: {rooms.length}
        </b>
      </div>

      <Spin spinning={loading}>
        <Listy
          items={dataSource}
          rowKey='id'
          className={classes.common_list}
          itemRender={(item, index) => (
            <div
              key={item.id}
              className={classes.common_item}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                item?.invite_status === undefined &&
                onConfirmOpen('invite', item)
              }
            >
              <Typography.Text>{index + 1}.</Typography.Text>{' '}

              {item?.invite_status !== undefined
                ? inviteStatusIcons[item.invite_status]
                : null}{' '}

              {item.name}{' '}

              {item?.invite_status === 2 && (
                <Tooltip title={t('Users.Tabs.actions.reinvite')}>
                  <ReloadOutlined
                    className={classes.reinvite}
                    onClick={(e) => {
                      e.stopPropagation();
                      reInvite(item);
                    }}
                  />
                </Tooltip>
              )}{' '}

              {item?.invite_status !== undefined && (
                <CloseCircleOutlined
                  className={classes.close}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.invite_status !== 3) {
                      onConfirmOpen('delete', item);
                    }
                  }}
                />
              )}
            </div>
          )}
        />
      </Spin>

      {isOpenConfirm && (
        <Suspense fallback={<div />}>
          <ConfirmModal
            title={t('main.confirmAction')}
            bodyContent={conFirmCases[confirmStatus].body}
            open={isOpenConfirm}
            loading={loading}
            onClose={onCLose}
            onCancel={onCLose}
            okText={t('main.confirmButton')}
            onOk={conFirmCases[confirmStatus].action}
          />
        </Suspense>
      )}
    </>
  );
}
